import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
  useContext,
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import LoginRoutes from "routes/loginRouter";
import {
  checkEmailCompanyModel,
  getUserModel,
  loginCompanyModel,
  loginUserModel,
} from "models/userModel";
import { XmppContext } from "./xmppContext";

export const keyCookie = process.env.REACT_APP_KEY_COOKIE || "authUser";
export const authContext = createContext(null);

/* ---------------------------
 * Cookie Utilities
 * --------------------------- */
const cookie = {
  set(name, value, days = 7) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(
      JSON.stringify(value)
    )}; expires=${expires}; path=/`;
  },

  get(name) {
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    if (!cookie) return null;
    try {
      return JSON.parse(decodeURIComponent(cookie.split("=")[1]));
    } catch {
      return null;
    }
  },

  delete(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },
};

/* ---------------------------
 * Auth Provider
 * --------------------------- */
export const AuthProvider = ({ children }) => {
  const { connected, connect, reconnectTimeoutRef, disconnect } =
    useContext(XmppContext);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /* ---------------------------
   * Refresh user info from API
   * --------------------------- */
  const refreshUser = useCallback(async (storedCompany, storedUser) => {
    try {
      const res = await getUserModel(storedUser.token, storedUser.id);
      if (!res.success) {
        cookie.set(keyCookie, { company: company, user: null });
        window.location.reload();
        return;
      }

      const data = res.data;
      const updatedUser = {
        id: data.id,
        username: data.username,
        email: data.email,
        phoneNumber: data.phoneNumber,
        divisionName: data.namadivisi,
        companyName: data.namausaha,
        modules: data.modules,
        token: data.token,
      };

      setUser(updatedUser);
      cookie.set(keyCookie, { company: storedCompany, user: updatedUser });
      setIsLoading(false);
    } catch (err) {
      console.error("❌ Failed to refresh user:", err);
      setIsLoading(false);
    }
  }, []);

  const handleCheckEmailCompany = async (email) => {
    try {
      const res = await checkEmailCompanyModel(email);
      if (!res.success) return { success: false, message: res.message };

      return { success: true, message: res.message };
    } catch (err) {
      console.error("Check Email failed:", err);
      return { success: false, message: "Login failed, please try again." };
    }
  };

  /* ---------------------------
   * Login Company
   * --------------------------- */
  const handleLoginCompany = async (email, password) => {
    try {
      const res = await loginCompanyModel(email, password);
      if (!res.success) return { success: false, message: res.message };

      setCompany(res.data);
      cookie.set(keyCookie, { company: res.data, user: null });

      return { success: true, message: res.message };
    } catch (err) {
      console.error("Company login failed:", err);
      return { success: false, message: "Login failed, please try again." };
    }
  };

  /* ---------------------------
   * Login User (XMPP + API)
   * --------------------------- */
  const handleLoginUser = async (token, phone, password) => {
    try {
      const xmppJid = `${phone}@chat.cuaps.id`;

      console.log(xmppJid);
      // Try to connect to XMPP with 5 second timeout, but don't block login if it fails
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("XMPP connection timeout")), 5000)
        );
        const xmppResult = await Promise.race([
          connect(xmppJid, password),
          timeoutPromise,
        ]);
        if (xmppResult && xmppResult.success === false) {
          console.warn("⚠️ XMPP connection failed, but continuing with login");
        }
      } catch (xmppErr) {
        console.warn(
          "⚠️ XMPP connection error, but continuing with login:",
          xmppErr.message
        );
      }

      const res = await loginUserModel(token, phone, password);
      if (!res.success) return { success: false, message: res.message };

      const data = res.data;
      setUser({ jid: xmppJid, ...data });
      cookie.set(keyCookie, { company, user: data });

      return { success: true, message: res.message };
    } catch (err) {
      console.error("User login failed:", err);

      return { success: false, message: "Login failed, please try again." };
    }
  };

  /* ---------------------------
   * Update local user info
   * --------------------------- */
  const handleUpdate = useCallback((username, phoneNumber) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, username, phoneNumber };
      cookie.set(keyCookie, { company: company, user: updated });
      return updated;
    });
  }, []);

  /* ---------------------------
   * Logout
   * --------------------------- */
  const handleLogoutUser = useCallback(() => {
    cookie.set(keyCookie, { company: company, user: null });
    disconnect();
    startTransition(() => {
      setUser(null);
      navigate("/");
    });
  }, [disconnect, navigate]);

  const handleLogoutCompany = () => {
    cookie.set(keyCookie, { company: null, user: null });
    setCompany(null);
  };
  /* ---------------------------
   * Memoized context value
   * --------------------------- */
  const contextValue = useMemo(
    () => ({
      user,
      company,
      setCompany,
      isLoading,
      handleLoginUser,
      handleLoginCompany,
      handleCheckEmailCompany,
      handleLogoutUser,
      handleLogoutCompany,
      handleUpdate,
    }),
    [
      user,
      company,
      isLoading,
      handleLoginUser,
      handleLoginCompany,
      handleLogoutUser,
      handleLogoutCompany,
      handleUpdate,
    ]
  );

  /* ---------------------------
   * Effects (bottom section)
   * --------------------------- */

  // Initialize user from cookie
  useEffect(() => {
    const storedUser = cookie.get(keyCookie);

    if (storedUser?.company) setCompany(storedUser.company);
    if (storedUser?.user) {
      refreshUser(storedUser.company, storedUser.user);
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    if (!user || connected) return;

    const autoConnectXmpp = async () => {
      try {
        const token = decodeURIComponent(user.token);
        const decoded = jwtDecode(token);
        const xmppJid = `${user.phoneNumber}@chat.cuaps.id`;
        await connect(xmppJid, decoded.password);
      } catch (err) {
        console.warn("⚠️ Failed to auto-connect XMPP:", err);
      }
    };

    autoConnectXmpp();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user, connect, reconnectTimeoutRef, connected]);

  /* ---------------------------
   * Render
   * --------------------------- */
  if (isLoading) return <div>Loading...</div>;

  return (
    <authContext.Provider value={contextValue}>
      {user ? children : <LoginRoutes />}
    </authContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider;
